import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Eye, CheckCircle, XCircle, Trash2, Users, Clock, UserCheck, UserX, X, Edit } from 'lucide-react';
import userService from '../services/userService';
import { showSuccess, showError } from '../services/toastService';
import { getImageUrl } from '../utils/imageUrl';
import './UserManagement.css';

// UI Components
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import InfiniteScrollSentinel from './InfiniteScrollSentinel';

const UserManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [buyers, setBuyers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState(() => searchParams.get('status') || 'all');
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('search') || '');
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [buyerToApprove, setBuyerToApprove] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [buyerToReject, setBuyerToReject] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteBuyerId, setDeleteBuyerId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingBuyer, setEditingBuyer] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (filterStatus !== 'all') next.set('status', filterStatus); else next.delete('status');
      if (searchQuery) next.set('search', searchQuery); else next.delete('search');
      return next;
    }, { replace: true });
    setPagination((current) => ({ ...current, page: 1 }));
    fetchBuyers(1);
  }, [filterStatus, searchQuery, setSearchParams]);

  useEffect(() => {
    if (pagination.page > 1) fetchBuyers(pagination.page);
  }, [pagination.page]);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchBuyers = async (pageNum = 1) => {
    setLoading(true);
    try {
      const params = {
        page: pageNum,
        limit: pagination.limit
      };
      if (filterStatus !== 'all') {
        params.approval_status = filterStatus;
      }
      if (searchQuery) {
        params.search = searchQuery;
      }
      const response = await userService.getAllBuyers(params);
      setBuyers((current) => pageNum === 1 ? response.data.buyers : [...current, ...response.data.buyers]);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching buyers:', error);
      showError(error.message || 'Failed to fetch buyers');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await userService.getBuyerStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleClearFilters = () => {
    setFilterStatus('all');
    setSearchQuery('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleViewDetails = async (buyer) => {
    try {
      const response = await userService.getBuyer(buyer.id);
      console.log('🔍 ADMIN PANEL - API Response:', response);
      console.log('🔍 ADMIN PANEL - Buyer Data:', response.data.buyer);
      console.log('🔍 ADMIN PANEL - Address:', response.data.buyer.address);
      console.log('🔍 ADMIN PANEL - City:', response.data.buyer.city);
      console.log('🔍 ADMIN PANEL - State:', response.data.buyer.state);
      console.log('🔍 ADMIN PANEL - Pincode:', response.data.buyer.pincode);
      console.log('🔍 ADMIN PANEL - Business Proof:', response.data.buyer.business_proof);
      setSelectedBuyer(response.data.buyer);
      setShowDetailsModal(true);
    } catch (error) {
      showError(error.message || 'Failed to fetch buyer details');
    }
  };

  const handleApprove = async (buyerId) => {
    setBuyerToApprove(buyers.find(buyer => buyer.id === buyerId));
    setShowApprovalModal(true);
  };

  const handleApprovalConfirm = async () => {
    if (!buyerToApprove) return;

    setLoading(true);
    try {
      await userService.approveBuyer(buyerToApprove.id);
      showSuccess('Buyer approved successfully! Approval email sent.');
      fetchBuyers(pagination.page);
      fetchStats();
      setShowApprovalModal(false);
      setBuyerToApprove(null);
      if (showDetailsModal) {
        setShowDetailsModal(false);
      }
    } catch (error) {
      showError(error.message || 'Failed to approve buyer');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectClick = (buyer) => {
    setBuyerToReject(buyer);
    setRejectReason('');
    setShowRejectModal(true);
  };

  const handleRejectConfirm = async () => {
    if (!rejectReason.trim()) {
      showError('Please provide a reason for rejection');
      return;
    }

    setLoading(true);
    try {
      await userService.rejectBuyer(buyerToReject.id, rejectReason);
      showSuccess('Buyer rejected successfully! Rejection email sent.');
      fetchBuyers(pagination.page);
      fetchStats();
      setShowRejectModal(false);
      setBuyerToReject(null);
      setRejectReason('');
      if (showDetailsModal) {
        setShowDetailsModal(false);
      }
    } catch (error) {
      showError(error.message || 'Failed to reject buyer');
    } finally {
      setLoading(false);
    }
  };



  const handleDeleteClick = (buyerId) => {
    setDeleteBuyerId(buyerId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setLoading(true);
    try {
      const response = await userService.deleteBuyer(deleteBuyerId);
      showSuccess('Buyer deleted successfully!');
      fetchBuyers(1);
      fetchStats();
      setDeleteDialogOpen(false);
      setDeleteBuyerId(null);
    } catch (error) {
      // Show user-friendly error message
      const errorMessage = error.message || 'Failed to delete buyer';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (buyer) => {
    const action = buyer.is_active ? 'deactivate' : 'activate';
    const confirmMessage = buyer.is_active
      ? `Are you sure you want to deactivate ${buyer.business_name}? They will not be able to login or place orders.`
      : `Are you sure you want to activate ${buyer.business_name}? They will be able to login and place orders.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(true);
    try {
      await userService.toggleBuyerActive(buyer.id);
      showSuccess(`Buyer ${action}d successfully!`);
      fetchBuyers(pagination.page);
      fetchStats();
      if (showDetailsModal && selectedBuyer?.id === buyer.id) {
        const response = await userService.getBuyer(buyer.id);
        setSelectedBuyer(response.data.buyer);
      }
    } catch (error) {
      showError(error.message || `Failed to ${action} buyer`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (buyer) => {
    setEditingBuyer(buyer);
    setEditFormData({
      business_name: buyer.business_name,
      contact_person: buyer.contact_person,
      email: buyer.email,
      gst_number: buyer.gst_number,
      address: buyer.address,
      city: buyer.city,
      state: buyer.state,
      pincode: buyer.pincode,
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await userService.updateBuyer(editingBuyer.id, editFormData);
      showSuccess('Buyer updated successfully!');
      fetchBuyers(pagination.page);
      setShowEditModal(false);
      setEditingBuyer(null);
      if (showDetailsModal && selectedBuyer?.id === editingBuyer.id) {
        const response = await userService.getBuyer(editingBuyer.id);
        setSelectedBuyer(response.data.buyer);
      }
    } catch (error) {
      showError(error.message || 'Failed to update buyer');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'pending',
      approved: 'active',
      rejected: 'destructive'
    };
    const texts = {
      pending: 'PENDING',
      approved: 'APPROVED',
      rejected: 'REJECTED'
    };
    return (
      <Badge variant={variants[status] || 'pending'}>
        {texts[status] || 'PENDING'}
      </Badge>
    );
  };

  const getDocumentStatusBadge = (documentsUploaded) => {
    if (documentsUploaded) {
      return null; // Don't show badge if documents are uploaded
    }
    return (
      <Badge variant="warning" style={{ marginLeft: '8px', fontSize: '11px' }}>
        DOCS PENDING
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';

      return date.toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatPhone = (phone, countryCode = '+91') => {
    if (!phone) return 'N/A';
    // If phone already has country code, return as is
    if (phone.startsWith('+')) return phone;
    // Otherwise, prepend country code
    return `${countryCode} ${phone}`;
  };

  return (
    <div className="category-management-container">
      {/* Header */}
      <div className="cm-header">
        <div>
          <h1 className="cm-title">User Management</h1>
          <p className="cm-subtitle">Manage buyer registrations and approvals</p>
        </div>
      </div>

      {/* Statistics */}
      {stats && (
        <div className="um-stats-grid">
          <Card>
            <CardContent className="um-stat-card">
              <div className="um-stat-icon">
                <Users size={20} />
              </div>
              <div className="um-stat-info">
                <div className="um-stat-number">{stats.total}</div>
                <div className="um-stat-label">Total Buyers</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="um-stat-card">
              <div className="um-stat-icon um-stat-pending">
                <Clock size={20} />
              </div>
              <div className="um-stat-info">
                <div className="um-stat-number">{stats.pending}</div>
                <div className="um-stat-label">Pending Approval</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="um-stat-card">
              <div className="um-stat-icon um-stat-approved">
                <UserCheck size={20} />
              </div>
              <div className="um-stat-info">
                <div className="um-stat-number">{stats.approved}</div>
                <div className="um-stat-label">Approved</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="um-stat-card">
              <div className="um-stat-icon um-stat-rejected">
                <UserX size={20} />
              </div>
              <div className="um-stat-info">
                <div className="um-stat-number">{stats.rejected}</div>
                <div className="um-stat-label">Rejected</div>
              </div>
            </CardContent>
          </Card>
          {stats.pendingDocuments > 0 && (
            <Card>
              <CardContent className="um-stat-card">
                <div className="um-stat-icon" style={{ backgroundColor: '#fef3c7', color: '#f59e0b' }}>
                  <Clock size={20} />
                </div>
                <div className="um-stat-info">
                  <div className="um-stat-number">{stats.pendingDocuments}</div>
                  <div className="um-stat-label">Pending Documents</div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Users List Section */}
      <Card>
        <CardHeader>
          <CardTitle>User List</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="um-filters">
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by business name, phone, GST..."
              className="um-search-input"
            />
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="pm-select-filter">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All ({stats?.total || 0})</SelectItem>
                <SelectItem value="pending">Pending ({stats?.pending || 0})</SelectItem>
                <SelectItem value="approved">Approved ({stats?.approved || 0})</SelectItem>
                <SelectItem value="rejected">Rejected ({stats?.rejected || 0})</SelectItem>
              </SelectContent>
            </Select>
            <Button
              onClick={handleClearFilters}
              disabled={filterStatus === 'all' && searchQuery === ''}
              className="um-clear-filters-btn"
              style={{
                backgroundColor: (filterStatus !== 'all' || searchQuery !== '') ? '#111827' : '#d1d5db',
                color: '#ffffff',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '6px',
                cursor: (filterStatus !== 'all' || searchQuery !== '') ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '14px',
                fontWeight: '500',
                opacity: (filterStatus !== 'all' || searchQuery !== '') ? 1 : 0.6
              }}
            >
              <X size={16} />
              Clear Filters
            </Button>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Business Details</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>GST Number</TableHead>
                <TableHead>Approval Status</TableHead>
                <TableHead>Active Status</TableHead>
                <TableHead>Registered</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && buyers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan="8" className="cm-table-empty">Loading...</TableCell>
                </TableRow>
              ) : buyers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan="8" className="cm-table-empty">No buyers found</TableCell>
                </TableRow>
              ) : (
                buyers.map(buyer => (
                  <TableRow key={buyer.id}>
                    <TableCell className="cm-id-cell">{buyer.id}</TableCell>
                    <TableCell>
                      <div className="um-business-name">{buyer.business_name}</div>
                      <div className="um-location">{buyer.city}, {buyer.state}</div>
                    </TableCell>
                    <TableCell>
                      <div className="um-contact-person">{buyer.contact_person}</div>
                      <div className="um-phone" style={{ fontWeight: '600', color: '#111827', marginTop: '4px' }}>
                        {formatPhone(buyer.phone, buyer.country_code)}
                      </div>
                      {buyer.email && (
                        <div className="um-email" style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                          {buyer.email}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="um-gst">{buyer.gst_number || 'N/A'}</TableCell>
                    <TableCell>
                      {getStatusBadge(buyer.approval_status)}
                      {getDocumentStatusBadge(buyer.documents_uploaded)}
                    </TableCell>
                    <TableCell>
                      <label className="pm-toggle" title={buyer.is_active ? 'Deactivate User' : 'Activate User'}>
                        <input
                          type="checkbox"
                          checked={buyer.is_active}
                          onChange={() => handleToggleActive(buyer)}
                        />
                        <span className="pm-toggle-slider"></span>
                      </label>
                    </TableCell>
                    <TableCell className="um-date">{formatDate(buyer.createdAt)}</TableCell>
                    <TableCell>
                      <div className="cm-actions">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewDetails(buyer)}
                          className="cm-edit-btn"
                          title="View Details"
                        >
                          <Eye size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditClick(buyer)}
                          className="cm-edit-btn"
                          title="Edit User"
                        >
                          <Edit size={16} />
                        </Button>
                        {buyer.approval_status === 'pending' && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleApprove(buyer.id)}
                              className="um-approve-btn"
                              title="Approve User"
                            >
                              <CheckCircle size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRejectClick(buyer)}
                              className="um-reject-btn"
                              title="Reject User"
                            >
                              <XCircle size={16} />
                            </Button>
                          </>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(buyer.id)}
                          className="cm-delete-btn"
                          title="Delete User"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          <InfiniteScrollSentinel
            hasMore={pagination.page < pagination.pages}
            loading={loading}
            onLoadMore={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
          />
        </CardContent>
      </Card>

      {/* Buyer Details Modal - Redesigned */}
      {showDetailsModal && selectedBuyer && (
        <div className="modal-overlay-new" onClick={() => setShowDetailsModal(false)}>
          <div className="modal-content-new" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="modal-header-new">
              <div className="modal-header-content">
                <h2 className="modal-title-new">Buyer Details</h2>
                <div className="modal-status-badge">
                  {getStatusBadge(selectedBuyer.approval_status)}
                  {getDocumentStatusBadge(selectedBuyer.documents_uploaded)}
                </div>
              </div>
              <button className="modal-close-new" onClick={() => setShowDetailsModal(false)}>
                <X size={24} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="modal-body-new">
              {/* Business Card */}
              <div className="info-card">
                <div className="info-card-header">
                  <h3 className="info-card-title">Business Information</h3>
                </div>
                <div className="info-card-body">
                  <div className="info-grid-2col">
                    <div className="info-item">
                      <label className="info-label">Business Name</label>
                      <p className="info-value">{selectedBuyer.business_name || 'Not provided'}</p>
                    </div>
                    <div className="info-item">
                      <label className="info-label">GST Number</label>
                      <p className="info-value info-value-mono">{selectedBuyer.gst_number || 'Not provided'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Documents Card */}
              <div className="info-card">
                <div className="info-card-header">
                  <h3 className="info-card-title">Documents</h3>
                </div>
                <div className="info-card-body">
                  <div className="documents-grid">
                    {/* Business Proof */}
                    <div className="document-item">
                      <label className="document-label">Business Proof</label>
                      {selectedBuyer.business_proof ? (
                        <a
                          href={getImageUrl(selectedBuyer.business_proof)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="document-preview"
                        >
                          {selectedBuyer.business_proof.toLowerCase().includes('.pdf') ? (
                            <div className="document-pdf">
                              <span className="pdf-icon">📄</span>
                              <span className="pdf-text">View PDF</span>
                            </div>
                          ) : (
                            <img
                              src={getImageUrl(selectedBuyer.business_proof)}
                              alt="Business Proof"
                              className="document-image"
                            />
                          )}
                        </a>
                      ) : (
                        <div className="document-placeholder">
                          <Users size={32} />
                          <span>No document</span>
                        </div>
                      )}
                    </div>

                    {/* Aadhar Card */}
                    <div className="document-item">
                      <label className="document-label">Aadhar Card</label>
                      {selectedBuyer.aadhar_card ? (
                        <a
                          href={getImageUrl(selectedBuyer.aadhar_card)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="document-preview"
                        >
                          <img
                            src={getImageUrl(selectedBuyer.aadhar_card)}
                            alt="Aadhar Card"
                            className="document-image"
                          />
                        </a>
                      ) : (
                        <div className="document-placeholder">
                          <Users size={32} />
                          <span>No document</span>
                        </div>
                      )}
                    </div>

                    {/* Owner Photo */}
                    <div className="document-item">
                      <label className="document-label">Owner Photo</label>
                      {selectedBuyer.owner_photo ? (
                        <a
                          href={getImageUrl(selectedBuyer.owner_photo)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="document-preview"
                        >
                          <img
                            src={getImageUrl(selectedBuyer.owner_photo)}
                            alt="Owner Photo"
                            className="document-image"
                          />
                        </a>
                      ) : (
                        <div className="document-placeholder">
                          <Users size={32} />
                          <span>No photo</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Card */}
              <div className="info-card">
                <div className="info-card-header">
                  <h3 className="info-card-title">Contact Information</h3>
                </div>
                <div className="info-card-body">
                  <div className="info-grid-3col">
                    <div className="info-item">
                      <label className="info-label">Contact Person</label>
                      <p className="info-value">{selectedBuyer.contact_person || 'Not provided'}</p>
                    </div>
                    <div className="info-item">
                      <label className="info-label">Phone Number</label>
                      <p className="info-value" style={{ fontWeight: '600', fontSize: '15px' }}>
                        {formatPhone(selectedBuyer.phone, selectedBuyer.country_code)}
                      </p>
                    </div>
                    <div className="info-item">
                      <label className="info-label">Email</label>
                      <p className="info-value">{selectedBuyer.email || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Address Card */}
              <div className="info-card">
                <div className="info-card-header">
                  <h3 className="info-card-title">Address</h3>
                </div>
                <div className="info-card-body">
                  <div className="address-content">
                    <p className="address-line">{selectedBuyer.address || 'Address not provided'}</p>
                    <p className="address-line">{[selectedBuyer.city, selectedBuyer.state, selectedBuyer.pincode].filter(Boolean).join(', ') || 'Location not provided'}</p>
                  </div>
                </div>
              </div>

              {/* Registration Card */}
              <div className="info-card">
                <div className="info-card-header">
                  <h3 className="info-card-title">Registration Details</h3>
                </div>
                <div className="info-card-body">
                  <div className="info-grid-2col">
                    <div className="info-item">
                      <label className="info-label">Registered On</label>
                      <p className="info-value">{formatDate(selectedBuyer.createdAt)}</p>
                    </div>
                    <div className="info-item">
                      <label className="info-label">Last Updated</label>
                      <p className="info-value">{formatDate(selectedBuyer.updatedAt)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="modal-footer-new">
              {selectedBuyer.approval_status === 'pending' && (
                <>
                  <button
                    className="btn-approve-new"
                    onClick={() => handleApprove(selectedBuyer.id)}
                    disabled={loading}
                  >
                    <CheckCircle size={18} />
                    <span>Approve Buyer</span>
                  </button>
                  <button
                    className="btn-reject-new"
                    onClick={() => handleRejectClick(selectedBuyer)}
                    disabled={loading}
                  >
                    <XCircle size={18} />
                    <span>Reject Buyer</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && buyerToReject && (
        <div className="modal-overlay" onClick={() => setShowRejectModal(false)}>
          <div className="modal-content reject-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Reject Buyer</h2>
              <button className="modal-close" onClick={() => setShowRejectModal(false)}>×</button>
            </div>
            <div className="modal-body rejection-modal-body">
              <div className="rejection-confirmation-content">
                <div className="rejection-banner">
                  <span className="rejection-icon"><XCircle size={20} /></span>
                  <div>
                    <h3>Reject {buyerToReject.business_name || 'this buyer'}?</h3>
                    <p>The buyer will not be able to access marketplace purchasing.</p>
                  </div>
                </div>

                <div className="buyer-summary rejection-buyer-summary">
                  <div className="buyer-summary-field">
                    <span>Business</span>
                    <strong>{buyerToReject.business_name || 'Not provided'}</strong>
                  </div>
                  <div className="buyer-summary-field">
                    <span>Phone</span>
                    <strong>{formatPhone(buyerToReject.phone, buyerToReject.country_code)}</strong>
                  </div>
                  <div className="buyer-summary-field">
                    <span>Email</span>
                    <strong>{buyerToReject.email || 'Not provided'}</strong>
                  </div>
                  <div className="buyer-summary-field">
                    <span>GST Number</span>
                    <strong>{buyerToReject.gst_number || 'Not provided'}</strong>
                  </div>
                </div>

                <div className="rejection-reason-field">
                  <label>Reason for rejection <span>*</span></label>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Enter a clear reason. This will be shared with the buyer."
                    rows="3"
                    required
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer rejection-modal-footer">
              <button className="btn-secondary" onClick={() => setShowRejectModal(false)}>
                Cancel
              </button>
              <button
                className="btn-reject"
                onClick={handleRejectConfirm}
                disabled={loading || !rejectReason.trim()}
              >
                <XCircle size={16} />
                {loading ? 'Rejecting…' : 'Reject Buyer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approval Confirmation Modal */}
      {showApprovalModal && buyerToApprove && (
        <div className="modal-overlay" onClick={() => setShowApprovalModal(false)}>
          <div className="modal-content approval-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Approve Buyer</h2>
              <button className="modal-close" onClick={() => setShowApprovalModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="approval-confirmation-content">
                <div className="approval-banner">
                  <span className="approval-icon">
                    <CheckCircle size={20} />
                  </span>
                  <div className="approval-details">
                    <h3>Approve {buyerToApprove.business_name || 'this buyer'}?</h3>
                    <p>This buyer will get marketplace access immediately.</p>
                  </div>
                </div>

                <div className="buyer-summary">
                  <div className="buyer-summary-field">
                    <span>Business</span>
                    <strong>{buyerToApprove.business_name || 'Not provided'}</strong>
                  </div>
                  <div className="buyer-summary-field">
                    <span>Phone</span>
                    <strong>{formatPhone(buyerToApprove.phone, buyerToApprove.country_code)}</strong>
                  </div>
                  <div className="buyer-summary-field">
                    <span>Email</span>
                    <strong>{buyerToApprove.email || 'Not provided'}</strong>
                  </div>
                  <div className="buyer-summary-field">
                    <span>GST Number</span>
                    <strong>{buyerToApprove.gst_number || 'Not provided'}</strong>
                  </div>
                </div>

                <div className="approval-note">
                  <strong>After approval</strong>
                  <span>The buyer can view pricing, add products to cart and place orders.</span>
                </div>
              </div>
            </div>
            <div className="modal-footer approval-modal-footer">
              <button
                className="btn-secondary"
                onClick={() => setShowApprovalModal(false)}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="btn-approve"
                onClick={handleApprovalConfirm}
                disabled={loading}
              >
                <CheckCircle size={16} />
                {loading ? 'Approving…' : 'Approve Buyer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this user? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="cm-dialog-actions">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="cm-delete-btn">
              Delete
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit User Modal */}
      {showEditModal && editingBuyer && (
        <div className="modal-overlay-new" onClick={() => setShowEditModal(false)}>
          <div className="modal-content-new" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="modal-header-new">
              <div className="modal-header-content">
                <h2 className="modal-title-new">Edit Buyer</h2>
              </div>
              <button className="modal-close-new" onClick={() => setShowEditModal(false)}>
                <X size={24} />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleEditSubmit}>
              <div className="modal-body-new">
                {/* Business Info */}
                <div className="info-card">
                  <div className="info-card-header">
                    <h3 className="info-card-title">Business Information</h3>
                  </div>
                  <div className="info-card-body">
                    <div className="info-grid-2col">
                      <div className="info-item">
                        <label className="info-label">Business Name *</label>
                        <Input
                          type="text"
                          value={editFormData.business_name}
                          onChange={(e) => setEditFormData({ ...editFormData, business_name: e.target.value })}
                          required
                        />
                      </div>
                      <div className="info-item">
                        <label className="info-label">GST Number</label>
                        <Input
                          type="text"
                          value={editFormData.gst_number}
                          onChange={(e) => setEditFormData({ ...editFormData, gst_number: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="info-card">
                  <div className="info-card-header">
                    <h3 className="info-card-title">Contact Information</h3>
                  </div>
                  <div className="info-card-body">
                    <div className="info-grid-2col">
                      <div className="info-item">
                        <label className="info-label">Contact Person *</label>
                        <Input
                          type="text"
                          value={editFormData.contact_person}
                          onChange={(e) => setEditFormData({ ...editFormData, contact_person: e.target.value })}
                          required
                        />
                      </div>
                      <div className="info-item">
                        <label className="info-label">Email</label>
                        <Input
                          type="email"
                          value={editFormData.email}
                          onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Address Info */}
                <div className="info-card">
                  <div className="info-card-header">
                    <h3 className="info-card-title">Address</h3>
                  </div>
                  <div className="info-card-body">
                    <div className="info-item" style={{ marginBottom: '16px' }}>
                      <label className="info-label">Address *</label>
                      <Input
                        type="text"
                        value={editFormData.address}
                        onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
                        required
                      />
                    </div>
                    <div className="info-grid-3col">
                      <div className="info-item">
                        <label className="info-label">City *</label>
                        <Input
                          type="text"
                          value={editFormData.city}
                          onChange={(e) => setEditFormData({ ...editFormData, city: e.target.value })}
                          required
                        />
                      </div>
                      <div className="info-item">
                        <label className="info-label">State *</label>
                        <Input
                          type="text"
                          value={editFormData.state}
                          onChange={(e) => setEditFormData({ ...editFormData, state: e.target.value })}
                          required
                        />
                      </div>
                      <div className="info-item">
                        <label className="info-label">Pincode *</label>
                        <Input
                          type="text"
                          value={editFormData.pincode}
                          onChange={(e) => setEditFormData({ ...editFormData, pincode: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="modal-footer-new">
                <button
                  type="submit"
                  className="btn-update-buyer"
                  disabled={loading}
                >
                  <Edit size={18} />
                  <span>{loading ? 'Updating...' : 'Update Buyer'}</span>
                </button>
                <button
                  type="button"
                  className="btn-secondary-new"
                  onClick={() => setShowEditModal(false)}
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
